import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, T } from "../data/content";
import { useLang } from "../context/LangContext";
import { useSiteData } from "../context/SiteDataContext";
import { WhatsAppIcon, Phone } from "./icons";

const links = [
  { id: "story", label: T.nav.about },
  { id: "services", label: T.nav.services },
  { id: "gallery", label: T.nav.gallery },
  { id: "visit", label: T.nav.visit },
];

export const Nav = () => {
  const { lang, toggle, tr } = useLang();
  const { brand } = useSiteData();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-[#D4AF37]/15 py-3" : "bg-transparent border-b border-transparent py-5"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between">
        <button onClick={() => scrollTo("hero")} className="flex items-center gap-3 group" data-testid="nav-logo">
          <img src={brand.logo} alt="Slaly Hair & Beauty logo" className="h-12 w-auto rounded-md bg-[#F5ECDA] p-1 object-contain shadow-md transition-transform duration-300 group-hover:scale-[1.03]" />
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="nav-link text-xs uppercase tracking-[0.2em] text-[#B7A78C] hover:text-[#F5ECDA] transition-colors duration-300"
              data-testid={`nav-${l.id}`}
            >
              {tr(l.label)}
            </button>
          ))}
          <a href="/products" className="nav-link text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#F3E5AB] transition-colors duration-300" data-testid="nav-shop">
            {tr(T.nav.shop)}
          </a>
          <a href="/avis" className="nav-link text-xs uppercase tracking-[0.2em] text-[#B7A78C] hover:text-[#F5ECDA] transition-colors duration-300" data-testid="nav-reviews">
            {tr(T.nav.reviews)}
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="text-xs tracking-[0.15em] text-[#B7A78C] hover:text-[#D4AF37] transition-colors duration-300 mr-1"
            data-testid="lang-toggle"
          >
            <span className={lang === "en" ? "text-[#D4AF37]" : ""}>EN</span>
            <span className="mx-1 opacity-40">/</span>
            <span className={lang === "fr" ? "text-[#D4AF37]" : ""}>FR</span>
          </button>

          <a
            href={`https://wa.me/${brand.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-xs font-medium uppercase tracking-[0.15em] text-black hover:bg-[#F3E5AB] transition-colors duration-300"
            data-testid="nav-whatsapp-cta"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {tr(T.nav.book)}
          </a>

          <button
            onClick={() => setOpen(true)}
            className="lg:hidden text-[#F5ECDA] p-1"
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0C0806] flex flex-col p-6 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center mb-16">
              <span className="font-serif text-xl tracking-[0.25em] text-[#F5ECDA]">SLALY</span>
              <button onClick={() => setOpen(false)} className="text-[#F5ECDA]" data-testid="nav-menu-close" aria-label="Close menu">
                <X className="h-7 w-7" />
              </button>
            </div>
            <nav className="flex flex-col gap-8">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  onClick={() => scrollTo(l.id)}
                  className="text-left font-serif text-4xl text-[#F5ECDA]"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {tr(l.label)}
                </motion.button>
              ))}
              <a href="/products" className="text-left font-serif text-4xl text-[#D4AF37]" data-testid="mobile-nav-shop">
                {tr(T.nav.shop)}
              </a>
              <a href="/avis" className="text-left font-serif text-4xl text-[#F5ECDA]" data-testid="mobile-nav-reviews">
                {tr(T.nav.reviews)}
              </a>
            </nav>
            <div className="mt-auto flex flex-col gap-3">
              <a href={`tel:${brand.tel}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-3.5 text-sm uppercase tracking-[0.15em] text-[#F5ECDA]" data-testid="mobile-call-cta">
                <Phone className="h-4 w-4" /> {brand.phoneDisplay}
              </a>
              <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-5 py-3.5 text-sm font-medium uppercase tracking-[0.15em] text-black" data-testid="mobile-whatsapp-cta">
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
